/* RESPONSIBLE TEAM: team-standalone */
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import Platform from './platform';
import type IntercomMessengerConfiguration from 'embercom/models/standalone/intercom-messenger-configuration';
import { IntegrationState, StandalonePlatform } from '../constants';
import type ApplicationInstance from '@ember/application/instance';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import SalesforceConversationExtractionRow from './component-data/salesforce/conversation-extraction-row';
import SalesforceKnowledgeHubModalData from './component-data/salesforce/knowledge-hub-modal';
import SalesforceConversationExcerpt from './component-data/salesforce/conversation-excerpt';
import SalesforceOnboardingGuideStep from './component-data/salesforce/onboarding-guide-step';
import SalesforceRequestAccessData from './component-data/salesforce/request-access';
import type ExternalFieldIdentifier from 'embercom/models/standalone/external-field-identifier';
import type Store from '@ember-data/store';

export default class SalesforcePlatform extends Platform {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare store: Store;

  salesforceConfig?: SalesforceConfiguration;
  intercomMessengerConfig?: IntercomMessengerConfiguration;

  constructor(
    owner: ApplicationInstance,
    inputs: {
      salesforceConfig?: SalesforceConfiguration;
      intercomMessengerConfig?: IntercomMessengerConfiguration;
    },
  ) {
    super(owner);
    this.salesforceConfig = inputs.salesforceConfig;
    this.intercomMessengerConfig = inputs.intercomMessengerConfig;
  }

  get componentData(): Platform['componentData'] {
    return {
      'conversation-extraction': new SalesforceConversationExtractionRow(this),
      'knowledge-hub-modal': new SalesforceKnowledgeHubModalData(this),
      'conversation-excerpt': new SalesforceConversationExcerpt(this),
      'onboarding-guide-step': new SalesforceOnboardingGuideStep(this),
      'request-access': new SalesforceRequestAccessData(this),
    } as const;
  }

  async loadConfigData() {
    // TODO
  }

  async loadRequiredData() {
    // TODO
  }

  async loadDataFields() {
    // TODO
  }

  get isConnected() {
    return this.salesforceConfig?.isConnected || false;
  }

  get hasInstalledCopilot() {
    return false;
  }

  get historicalSyncingEnabled() {
    // TODO: SA Generic platform migration – This isConversationSyncingEnabled is kind of circular, move it to to the base object?
    return (this.isConnected && this.finStandaloneService.isConversationSyncingEnabled) || false;
  }

  get finIsLive() {
    return (
      this.salesforceConfig?.isEnabled || this.intercomMessengerConfig?.isMessengerEnabled || false
    );
  }

  get finIsPaused() {
    return (
      this.salesforceConfig?.isPaused || this.intercomMessengerConfig?.isMessengerPaused || false
    );
  }

  get connectionState() {
    return this.calculateHighestIntegrationState([
      this.salesforceConfig?.status ?? IntegrationState.NotConfigured,
      this.intercomMessengerConfig?.integrationState ?? IntegrationState.NotConfigured,
    ]);
  }

  get icon() {
    return 'salesforce' as const;
  }

  get platformKey() {
    return StandalonePlatform.Salesforce;
  }

  get supportsFinPlayground() {
    return true;
  }

  get supportsConversationSyncing() {
    return true;
  }

  findMappedConversationField(id: string | undefined) {
    // TODO: SA Generic platform migration – Remove use of service here once we move data into this object
    return this.finStandaloneService.salesforceDataFields.caseFields.find(
      (field) => field.intercom_cvda?.id === id,
    );
  }

  isMappedUserAttribute(attributeIdentifier: string) {
    return this.finStandaloneService.salesforceDataFields.userFields.any(
      (userField) =>
        userField.intercom_cda?.identifier === attributeIdentifier ||
        userField.intercom_sda?.identifier === attributeIdentifier,
    );
  }

  isMappedCompanyAttribute(attributeIdentifier: string) {
    let externalFieldIdentifiers = this.store.peekAll(
      'standalone/external-field-identifier',
    ) as unknown as Array<ExternalFieldIdentifier>;

    return externalFieldIdentifiers.any(
      (externalFieldIdentifier) =>
        externalFieldIdentifier.intercomAttributeIdentifier === attributeIdentifier,
    );
  }

  isMappedConversationAttribute(attributeIdentifier: string) {
    return this.finStandaloneService.salesforceDataFields.caseFields.any(
      (ticketField) => `conversation.${ticketField.intercom_cvda?.id}` === attributeIdentifier,
    );
  }

  get ticketFields() {
    return this.finStandaloneService.salesforceDataFields.caseFields;
  }

  get userFields() {
    return this.finStandaloneService.salesforceDataFields.userFields;
  }

  get organizationFields() {
    return [];
  }
}
